import React from 'react';
import DashboardSkeleton from '../components/DashboardSkeleton';
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import MetricCard from '../components/MetricCard';
import RiskScore from '../components/RiskScore';
import AssetsSummary from '../components/AssetsSummary';
import { AssetOverviewCards } from '../components/AssetOverviewCards';
import CredentialLeaks from '../components/CredentialLeaks';
import DataLeaks from '../components/DataLeaks';
import RecentVulnerabilities from '../components/RecentVulnerabilities';
import TopVulnerableAssets from '../components/TopVulnerableAssets';
import RecentActivities from '../components/RecentActivities';
import AssetsDistributionMap from '../components/AssetsDistributionMap';
import TopTags from '../components/TopTags';
import TopTechnologies from '../components/TopTechnologies';
import { AlertOctagon, AlertTriangle, AlertCircle, Shield, Key, FileWarning } from 'lucide-react';
import { findingsTimeline } from '../data/findings';
import { regions } from '../data/regions';
import { tags } from '../data/tags';
import { technologies } from '../data/technologies';
import { recentActivities } from '../data/activities';
import { recentVulnerabilities, vulnerableAssets } from '../data/vulnerabilities';
import { Json, JSONApiResponse, MetricsApi, SecretsApi, CredentialsApi, TechnologiesApi } from 'api-client';
import { Technology } from 'api-client/models';
import { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import { getApiConfiguration } from '../api/utils';
import { useTeams } from '../contexts/TeamsContext';

const Dashboard = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentTeam } = useTeams();
  const teamSlug = currentTeam?.slug;
  const { token } = useContext(AuthContext); 
  const [statistics, setStatistics] = useState<Json | null>(null);
  const [credentialLeaksData, setCredentialLeaksData] = useState<any[]>([]);
  const [dataLeaksData, setDataLeaksData] = useState<any[]>([]);
  const [credentialLeaksCount, setCredentialLeaksCount] = useState<number>(0);
  const [dataLeaksCount, setDataLeaksCount] = useState<number>(0);
  const [newCredentialLeaks, setNewCredentialLeaks] = useState<number>(0);
  const [newDataLeaks, setNewDataLeaks] = useState<number>(0);
  const [topTechnologies, setTopTechnologies] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!teamSlug || !token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const metricsApi = new MetricsApi(getApiConfiguration(token));
        const response = await metricsApi.metricsDashboardRetrieveRaw({
          teamSlug,
        });
        const data = await response.raw.json();
        setStatistics(data);
        
        console.log('Dashboard data:', data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [teamSlug, token]);

  useEffect(() => {
    const fetchCredentialLeaks = async () => {
      if (!teamSlug || !token) {
        return;
      }

      try {
        const api = new CredentialsApi(getApiConfiguration(token));
        const response = await api.publicLeaksList({
          teamSlug,
          page: 1,
        });

        // Transform the data to match the expected format for the CredentialLeaks component
        const transformedLeaks = response.results.slice(0, 5).map(leak => ({
          id: leak.id,
          type: leak.sourceName || 'Password Leak',
          source: leak.source || 'Unknown Source',
          email: leak.identifier || 'N/A',
          password: leak.password || 'N/A',
          date: leak.createdAt ? new Date(leak.createdAt).toISOString().split('T')[0] : 'N/A',
          severity: 'medium', // Default severity since PublicLeak doesn't have a severity property
        }));

        setCredentialLeaksData(transformedLeaks);
        setCredentialLeaksCount(response.count);
        setNewCredentialLeaks(response.count); // This would be replaced with actual new leaks count
      } catch (err) {
        console.error('Error fetching credential leaks:', err);
      }
    };

    fetchCredentialLeaks();
  }, [teamSlug, token]);

  useEffect(() => {
    const fetchDataLeaks = async () => {
      if (!teamSlug || !token) {
        return;
      }

      try {
        const secretsApi = new SecretsApi(getApiConfiguration(token));
        const response = await secretsApi.dataLeaksList({
          teamSlug,
          // Add any filtering parameters if needed
        });

        // Transform the data to match the expected format for the DataLeaks component
        console.log('Data leaks response:', response);
        const transformedLeaks = response.results.slice(0, 5).map(leak => ({
          id: leak.id,
          type: leak.leakType || 'Unknown',
          source: leak.sourceLink || 'Unknown Source',
          fileName: leak.otherInfo?.file || 'Unknown File',
          tags: [],
          date: leak.createdAt ? new Date(leak.createdAt).toISOString().split('T')[0] : 'N/A',
          severity: (leak.severity || 'high') as 'high' | 'medium' | 'low'
        }));

        setDataLeaksData(transformedLeaks);
        setDataLeaksCount(response.count);
        
        // Calculate new leaks (leaks from the last 24 hours)
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const recentLeaks = response.results.filter(leak => 
          leak.createdAt && new Date(leak.createdAt) >= oneDayAgo
        ).length;
        setNewDataLeaks(recentLeaks);
      } catch (err) {
        console.error('Error fetching data leaks:', err);
      }
    };

    fetchDataLeaks();
  }, [teamSlug, token]);

  useEffect(() => {
    const fetchTopTechnologies = async () => {
      if (!teamSlug || !token) {
        return;
      }

      try {
        const api = new TechnologiesApi(getApiConfiguration(token));
        const response = await api.technologiesList({
          teamSlug,
          // The API returns technologies ordered by count by default
        });

        // Helper function to generate random colors for technologies
        const getRandomTechColor = () => {
          const colors = [
            'bg-blue-500/50',
            'bg-purple-500/50',
            'bg-green-500/50',
            'bg-yellow-500/50',
            'bg-red-500/50',
            'bg-indigo-500/50',
            'bg-pink-500/50',
            'bg-teal-500/50'
          ];
          return colors[Math.floor(Math.random() * colors.length)];
        };

        console.log('Top technologies response:', response);
        // Transform the data to match the expected format for the TopTechnologies component
        const transformedTechnologies = response.results.slice(0, 5).map(tech => ({
          id: tech.id.toString(),
          name: tech.name,
          category: tech.version || 'Unknown version',
          count: tech.assetsCount,
          percentage: Math.round((tech.assetsCount / Math.max(...response.results.map(t => t.assetsCount))) * 100),
          trend: 'stable', // We don't have trend data from the API
          color: getRandomTechColor(),
          icon: tech.logoPath || undefined
        }));

        setTopTechnologies(transformedTechnologies);
      } catch (err) {
        console.error('Error fetching top technologies:', err);
      }
    };

    fetchTopTechnologies();
  }, [teamSlug, token]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!statistics) {
    return <div>No dashboard data found</div>;
  }
  
  // Helper function to generate random colors for technologies
  const getRandomTechColor = () => {
    const colors = [
      'bg-blue-500/50',
      'bg-purple-500/50',
      'bg-green-500/50',
      'bg-yellow-500/50',
      'bg-red-500/50',
      'bg-indigo-500/50',
      'bg-pink-500/50',
      'bg-teal-500/50'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };
  const metrics = [
    {
      title: 'Critical Findings',
      value: statistics.severity_counts.critical,
      percentage: parseFloat(((statistics.severity_counts.critical / statistics.total_findings) * 100).toFixed(2)),
      icon: AlertOctagon,
      link: "/findings?severity=critical",
      color: { text: 'text-red-500', bg: 'bg-red-500' }
    },
    {
      title: 'High Findings',
      value: statistics.severity_counts.high,
      percentage: parseFloat(((statistics.severity_counts.high / statistics.total_findings) * 100).toFixed(2)),
      icon: AlertTriangle,
      link: "/findings?severity=high",
      color: { text: 'text-orange-500', bg: 'bg-orange-500' }
    },
    {
      title: 'Medium Findings',
      value: statistics.severity_counts.medium,
      percentage: parseFloat(((statistics.severity_counts.medium / statistics.total_findings) * 100).toFixed(2)),
      icon: AlertCircle,
      link: "/findings?severity=medium",
      color: { text: 'text-yellow-500', bg: 'bg-yellow-500' }
    },
    {
      title: 'Low Findings',
      value: statistics.severity_counts.low,
      percentage: parseFloat(((statistics.severity_counts.low / statistics.total_findings) * 100).toFixed(2)),
      icon: Shield,
      link: "/findings?severity=low",
      color: { text: 'text-emerald-500', bg: 'bg-emerald-500' }
    },
    {
      title: 'Credentials Leaks',
      value: statistics.credentials_stats.total_leaks,
      percentage: 100,
      icon: Key,
      link: "/data-protection/stealer-leaks",
      color: { text: 'text-blue-500', bg: 'bg-blue-500' }
    },
    {
      title: 'Sensitive Data',
      value: statistics.total_dataleaks,
      percentage: 100,
      icon: FileWarning,
      link: "/data-protection/data-leaks",
      color: { text: 'text-purple-500', bg: 'bg-purple-500' }
    }
  ];
  
  
  
  return (
    <div className="space-y-6">
      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {metrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Risk Score and Asset Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RiskScore
          score={statistics?.risk_score.toUpperCase()}
          totalFindings={statistics?.total_findings}
          openFindings={statistics?.status_counts.open}
          criticalRisks={statistics?.severity_counts.critical}
          highRisks={statistics?.severity_counts.high}
        />
        <AssetOverviewCards asset_counts={statistics?.asset_counts} />
      </div>

      {/* Vulnerabilities and Assets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentVulnerabilities vulnerabilities={statistics?.recent_findings} />
        <TopVulnerableAssets assets={statistics?.top_vulnerable_assets} />
      </div>

      Security Issues 
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CredentialLeaks
          leaks={credentialLeaksData.slice(0, 3).map(leak => ({
            ...leak,
            severity: leak.severity as 'high' | 'medium' | 'low'
          }))}
          totalLeaks={credentialLeaksCount}
          newLeaks={newCredentialLeaks || credentialLeaksCount}
        />
        <DataLeaks
          leaks={dataLeaksData.slice(0, 3).map(leak => ({
            ...leak,
            severity: leak.severity as 'high' | 'medium' | 'low'
          }))}
          totalLeaks={dataLeaksCount}
          newLeaks={newDataLeaks || dataLeaksCount}
        />
      </div>
     

      {/* Assets Distribution and Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <AssetsDistributionMap regions={regions} totalAssets={statistics?.asset_counts?.total || 0} />
        </div>
        
        <div className="space-y-6">
          <TopTags tags={tags} />
          <TopTechnologies technologies={topTechnologies} />
        </div>
      </div>

      {/* 


      Activity Feed 
      <RecentActivities 
        activities={recentActivities.map(activity => ({
          ...activity,
          type: activity.type as 'security' | 'system' | 'user' | 'scan'
        }))} 
      />
     */}


      {/* 
       * Findings Timeline Section
       * 
       * This component displays a stacked area chart visualizing the trend of security findings 
       * over the last 6 months, categorized by severity levels (Critical, High, Medium, Low).
       * 
       * The chart uses recharts' AreaChart with a responsive container to ensure proper sizing.
       * Each severity level is represented by a different colored area with consistent color coding:
       * - Critical: Red (#EF4444)
       * - High: Purple (#8B5CF6)
       * - Medium: Yellow (#EAB308)
       * - Low: Green (#10B981)
       * 
       * The data is provided through the findingsTimeline array which contains monthly data points
       * with counts for each severity level.
      
      <div className="bg-[#0F1629] rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Findings Timeline</h2>
          <span className="text-sm text-gray-500">Last 6 months</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={findingsTimeline}>
              <XAxis dataKey="name" stroke="#4B5563" />
              <YAxis stroke="#4B5563" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1A1F2E',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Area type="monotone" dataKey="Critical" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.5} />
              <Area type="monotone" dataKey="High" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.5} />
              <Area type="monotone" dataKey="Medium" stackId="1" stroke="#EAB308" fill="#EAB308" fillOpacity={0.5} />
              <Area type="monotone" dataKey="Low" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      */}
    </div>
  );
};

export default Dashboard;